import { invoiceBalance, invoicePaidAmount, quoteTotal, type Invoice, type Payment } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

const localDay = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(`${value.slice(0, 10)}T00:00:00`);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

export type ReceivableTiming = 'overdue' | 'today' | 'soon' | 'later';

export interface ReceivableRow {
  invoice: Invoice;
  customer: string;
  total: number;
  paid: number;
  balance: number;
  daysUntilDue: number;
  timing: ReceivableTiming;
}

export interface ReceivablesReport {
  rows: ReceivableRow[];
  outstanding: number;
  overdue: number;
  dueSoon: number;
  collected: number;
  billed: number;
  collectionRate: number;
  overdueCount: number;
  dueSoonCount: number;
}

export const buildReceivablesReport = (invoices: Invoice[], payments: Payment[], today = new Date()): ReceivablesReport => {
  const todayValue = localDay(today);
  const rows = invoices.flatMap((invoice): ReceivableRow[] => {
    const balance = invoiceBalance(invoice, payments);
    if (invoice.deletedAt || balance <= 0) return [];
    const daysUntilDue = Math.round((localDay(invoice.dueDate) - todayValue) / DAY_MS);
    const timing: ReceivableTiming = daysUntilDue < 0 ? 'overdue' : daysUntilDue === 0 ? 'today' : daysUntilDue <= 7 ? 'soon' : 'later';
    return [{
      invoice,
      customer: invoice.quoteSnapshot.customerName || 'ไม่ระบุลูกค้า',
      total: quoteTotal(invoice.quoteSnapshot),
      paid: invoicePaidAmount(invoice.id, payments),
      balance,
      daysUntilDue,
      timing,
    }];
  }).sort((left, right) => left.daysUntilDue - right.daysUntilDue || right.balance - left.balance);

  const billed = invoices.filter((invoice) => !invoice.deletedAt).reduce((sum, invoice) => sum + quoteTotal(invoice.quoteSnapshot), 0);
  const collected = invoices.filter((invoice) => !invoice.deletedAt).reduce((sum, invoice) => sum + invoicePaidAmount(invoice.id, payments), 0);
  const overdueRows = rows.filter((row) => row.timing === 'overdue');
  const dueSoonRows = rows.filter((row) => row.timing === 'today' || row.timing === 'soon');
  return {
    rows,
    outstanding: rows.reduce((sum, row) => sum + row.balance, 0),
    overdue: overdueRows.reduce((sum, row) => sum + row.balance, 0),
    dueSoon: dueSoonRows.reduce((sum, row) => sum + row.balance, 0),
    collected,
    billed,
    collectionRate: billed > 0 ? Math.min(100, (collected / billed) * 100) : 0,
    overdueCount: overdueRows.length,
    dueSoonCount: dueSoonRows.length,
  };
};
